// src/modules/taskRealtime/taskRealtimeReducers.ts
import {TaskRealtimeState, TaskRow} from './taskRealtimeTypes';

export const actionTypes = {
  RUNNING_FETCH_START: 'TASK_REALTIME_RUNNING_FETCH_START',
  RUNNING_FETCH_SUCCESS: 'TASK_REALTIME_RUNNING_FETCH_SUCCESS',
  RUNNING_FETCH_ERROR: 'TASK_REALTIME_RUNNING_FETCH_ERROR',
  RUNNING_CLEAR: 'TASK_REALTIME_RUNNING_CLEAR',

  SELECT_TASK: 'TASK_REALTIME_SELECT_TASK',
  SELECT_CLEAR: 'TASK_REALTIME_SELECT_CLEAR',

  DETAIL_FETCH_START: 'TASK_REALTIME_DETAIL_FETCH_START',
  DETAIL_FETCH_SUCCESS: 'TASK_REALTIME_DETAIL_FETCH_SUCCESS',
  DETAIL_FETCH_ERROR: 'TASK_REALTIME_DETAIL_FETCH_ERROR',

  DETAIL_SET_LAST_UPDATED_AT: 'TASK_REALTIME_DETAIL_SET_LAST_UPDATED_AT',
};

const getRowId = (r: any) => (r?.id ?? r?.Id ?? r?.['Id']) as string | undefined;

function mergeRowsById(prevRows: TaskRow[], nextRows: TaskRow[]) {
  const map = new Map<string, TaskRow>();
  for (const r of prevRows) {
    const id = getRowId(r);
    if (id) map.set(id, r);
  }
  for (const r of nextRows) {
    const id = getRowId(r);
    if (id) map.set(id, r);
  }
  return Array.from(map.values());
}

const initialData: TaskRealtimeState = {
  running: {
    status: 'idle',
    rows: [],
    error: null,
    taskType: null,
    report: null,
  },
  selected: {
    taskId: null,
    status: 'idle',
    row: null,
    error: null,
    lastUpdatedAt: null,
    taskType: null,
  },
};

export default function taskRealtimeReducer(state = initialData, action: any): TaskRealtimeState {
  // RESET_STATE deja `running`/`selected` en undefined (el slice sigue siendo un objeto).
  if (!state?.running || !state?.selected) {
    state = {
      running: state?.running ?? initialData.running,
      selected: state?.selected ?? initialData.selected,
    };
  }

  switch (action.type) {
    case actionTypes.RUNNING_FETCH_START: {
      const incomingType = action?.payload?.taskType ?? state.running.taskType;
      const typeChanged = !!incomingType && incomingType !== state.running.taskType;

      return {
        ...state,
        running: {
          ...state.running,
          status: 'loading',
          error: null,
          taskType: incomingType ?? state.running.taskType,
          ...(typeChanged ? {rows: [], report: null} : null),
        },
        ...(typeChanged ? {selected: {...initialData.selected, taskType: incomingType ?? null}} : null),
      };
    }

    case actionTypes.RUNNING_FETCH_SUCCESS: {
      const nextRows = action.payload?.rows ?? [];
      const incomingType = action.payload?.taskType ?? state.running.taskType;
      const typeChanged = !!incomingType && incomingType !== state.running.taskType;

      const shouldReplace = !!action.payload?.replace || typeChanged;

      const prevRows = Array.isArray(state.running.rows) ? state.running.rows : [];
      const safeNext = Array.isArray(nextRows) ? nextRows : [];
      const mergedRows = shouldReplace ? safeNext : mergeRowsById(prevRows, safeNext);

      const selectedRow = state.selected.taskId
        ? mergedRows.find((r: any) => getRowId(r) === state.selected.taskId) ?? null
        : state.selected.row;

      return {
        ...state,
        running: {
          ...state.running,
          status: 'success',
          rows: mergedRows,
          error: null,
          taskType: incomingType ?? state.running.taskType,
          report: action.payload?.report ?? (shouldReplace ? null : state.running.report),
        },
        selected: {
          ...state.selected,
          row: selectedRow,
        },
      };
    }

    case actionTypes.RUNNING_FETCH_ERROR:
      return {
        ...state,
        running: {
          ...state.running,
          status: 'error',
          error: action.payload,
        },
      };

    case actionTypes.RUNNING_CLEAR:
      return {
        ...state,
        running: {
          ...initialData.running,
        },
      };

    case actionTypes.SELECT_TASK:
      return {
        ...state,
        selected: {
          ...state.selected,
          taskId: action.payload,
          status: 'idle',
          error: null,
          // El row se mantiene hasta que llegue el detalle
          taskType: state.running.taskType,
        },
      };

    case actionTypes.SELECT_CLEAR:
      return {
        ...state,
        selected: {...initialData.selected, taskType: state.running.taskType},
      };

    case actionTypes.DETAIL_FETCH_START: {
      const incomingType = action?.payload?.taskType ?? state.selected.taskType;
      const typeChanged = !!incomingType && incomingType !== state.selected.taskType;

      return {
        ...state,
        selected: {
          ...state.selected,
          status: 'loading',
          error: null,
          taskType: incomingType ?? state.selected.taskType,
          ...(typeChanged ? {row: null, lastUpdatedAt: null, taskId: null} : null),
        },
      };
    }

    case actionTypes.DETAIL_FETCH_SUCCESS:
      return {
        ...state,
        selected: {
          ...state.selected,
          status: 'success',
          row: action.payload?.row ?? null,
          error: null,
          taskType: action.payload?.taskType ?? state.selected.taskType,
        },
      };

    case actionTypes.DETAIL_FETCH_ERROR:
      return {
        ...state,
        selected: {
          ...state.selected,
          status: 'error',
          error: action.payload,
        },
      };

    case actionTypes.DETAIL_SET_LAST_UPDATED_AT:
      return {
        ...state,
        selected: {
          ...state.selected,
          lastUpdatedAt: action.payload ?? null,
        },
      };

    default:
      return state;
  }
}
